import { FC, ReactNode, useState } from "react";
import propTypes from "prop-types";
import CheckIcon from "@untitled-ui/icons-react/build/esm/Check";
import type { SxProps } from "@mui/system/styleFunctionSx";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import SvgIcon from "@mui/material/SvgIcon";
import Typography from "@mui/material/Typography";
import type { Theme } from "@mui/material/styles/createTheme";
import { trackButtonClick } from "../trackinghelpers/trackinghelpers";
import { useTranslation } from "react-i18next";

interface PricingPlanProps {
  cta: string;
  currency: string;
  payment?: string;
  description: string;
  features: string[];
  icon: ReactNode;
  name: string;
  popular?: boolean;
  price?: string;
  textPrice?: string;
  sx?: SxProps<Theme>;
  link: string;
  onCtaClick?: (url: string) => void;
}

export const PricingPlan: FC<PricingPlanProps> = (props) => {
  const { t } = useTranslation();
  const {
    cta,
    currency,
    payment: _billingCycle,
    description,
    features,
    icon,
    name,
    popular,
    price,
    textPrice,
    sx,
    link,
    onCtaClick,
    ...other
  } = props;

  //  dviide price by 1,09 if current currency is euro
  const correctedPrice =
    currency === "$" ? (parseFloat(price || "0") / 1.09).toFixed(2) : price;

  // Yearly is billed as 12 × monthly (same per-month rate; no discount)
  const displayPrice = correctedPrice;
  return (
    <Card
      sx={{
        display: "flex",
        flexDirection: "column",
        ...sx,
      }}
      {...other}
    >
      <Box sx={{ p: 3 }}>
        <Typography sx={{ mt: 0 }} variant="h5">
          {name}
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 2 }} variant="body2">
          {description}
        </Typography>
        <Box sx={{ display: "flex", mt: 2 }}>
          {price ? (
            <>
              <Typography variant="h5">
                {currency}
                {displayPrice}
              </Typography>
              <Typography
                color="text.secondary"
                sx={{
                  alignSelf: "flex-end",
                  // ml: 0.5,
                }}
                variant="subtitle2"
              >
                {t("/month")}
              </Typography>
            </>
          ) : (
            <>{textPrice ? <>{textPrice}</> : <></>}</>
          )}
        </Box>
      </Box>
      <Divider />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          p: 3,
        }}
      >
        <Stack spacing={2} sx={{ flexGrow: 1 }}>
          {features.map((feature) => (
            <Stack
              alignItems="center"
              direction="row"
              spacing={1}
              key={feature}
              sx={{
                alignItems: "center",
                display: "flex",
              }}
            >
              <SvgIcon color="success">
                <CheckIcon />
              </SvgIcon>
              <Typography sx={{ fontWeight: 500 }} variant="body2">
                {feature}
              </Typography>
            </Stack>
          ))}
        </Stack>
        {link !== "monitoring" && link !== "coaching" && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: 6,
            }}
          >
            <Button
              onClick={() => {
                trackButtonClick("pricing-plan-cta" + name);
                // if (onCtaClick) {
                //   onCtaClick(link);
                // }
              }}
              href={link}
              target="_blank"
              fullWidth
              variant={popular ? "contained" : "outlined"}
            >
              {cta}
            </Button>
          </Box>
        )}
      </Box>
    </Card>
  );
};

PricingPlan.propTypes = {
  cta: propTypes.string.isRequired,
  currency: propTypes.string.isRequired,
  description: propTypes.string.isRequired,
  features: propTypes.array.isRequired,
  icon: propTypes.any.isRequired,
  name: propTypes.string.isRequired,
  popular: propTypes.bool,
  price: propTypes.string.isRequired,
  sx: propTypes.object,
};
